/**
 * Margins Service
 *
 * What a month billed against what it cost us: NMVTIS against AAMVA's graduated charge, MVR against each state's
 * fee. Revenue is what was actually billed (ApiRequestLogs.Cost); AAMVA's charge is estimated from the records
 * our own call log shows NMVTIS found, which is what AAMVA bills for.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';
import { BandRow } from './pricingTiersService';

export interface MarginBand {
  minQuantity: number;
  maxQuantity: number | null;
  ratePerUnit: number;
}

export interface MarginCost {
  hitCharges: number;
  minimumApplies: boolean;
  aamvaCost: number;
  margin: number;
  marginPerReport: number | null;
}

export type PricingSource = 'DefaultTiers' | 'OwnTiers' | 'Negotiated' | 'Flat';

export interface NmvtisClientMargin {
  clientId: string | null;
  clientName: string;
  reports: number;
  revenue: number;
  averagePrice: number | null;
  projectedReports: number;
  projectedRevenue: number;
  pricingSource: PricingSource;
}

export interface NmvtisMarginReport {
  year: number;
  month: number;
  isCurrentMonth: boolean;
  monthFractionElapsed: number;
  reports: number;
  revenue: number;
  aamvaCalls: number;
  recordsFound: number;
  scheduleEffectiveFrom: string | null;
  aamvaBands: MarginBand[];
  monthlyMinimum: number;
  monthlyServiceFee: number;
  actual: MarginCost;
  projection: { reports: number; recordsFound: number; revenue: number; cost: MarginCost } | null;
  defaultBands: MarginBand[];
  clients: NmvtisClientMargin[];
}

export interface MvrPricingRow {
  pricingId: string | null;
  recordType: string | null;
  price: number;
  stateFee: number | null;
  marginPerLookup: number | null;
}

export interface MvrStateMargin {
  stateCode: string;
  stateName: string;
  separateRecordPricing: boolean;
  pricingRows: MvrPricingRow[];
  lookups: number;
  revenue: number;
  stateCost: number | null;
  margin: number | null;
  feeUnknown: boolean;
  atOrBelowCost: boolean;
}

export interface MvrMarginReport {
  year: number;
  month: number;
  lookups: number;
  revenue: number;
  knownStateCost: number;
  revenueWithUnknownCost: number;
  states: MvrStateMargin[];
}

export interface AamvaScheduleResponse {
  schedules: { effectiveFrom: string; bands: MarginBand[] }[];
  monthlyMinimum: number;
  monthlyServiceFee: number;
}

const monthQuery = (year: number, month: number) => `?year=${year}&month=${month}`;

export const getNmvtisMargins = async (year: number, month: number): Promise<NmvtisMarginReport> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.MARGINS.NMVTIS + monthQuery(year, month));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const getMvrMargins = async (year: number, month: number): Promise<MvrMarginReport> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.MARGINS.MVR + monthQuery(year, month));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const updateStateFee = async (pricingId: string, stateFee: number | null): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.MARGINS.STATE_FEE(pricingId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stateFee }),
  });
  if (!response.ok) throw await createApiError(response);
};

export const getAamvaSchedule = async (): Promise<AamvaScheduleResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.MARGINS.AAMVA_SCHEDULE);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const saveAamvaSchedule = async (request: {
  effectiveFrom: string;
  bands: { minQuantity: number; maxQuantity: number | null; ratePerUnit: number; isActive: boolean }[];
  monthlyMinimum: number;
  monthlyServiceFee: number;
}): Promise<AamvaScheduleResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.MARGINS.AAMVA_SCHEDULE, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- Helpers ----

export const bandsToRows = (bands: MarginBand[]): BandRow[] =>
  bands.map((b) => ({
    minQuantity: String(b.minQuantity),
    maxQuantity: b.maxQuantity == null ? '' : String(b.maxQuantity),
    ratePerUnit: String(b.ratePerUnit),
    isActive: true,
    description: '',
  }));

/** Graduated total with rates used as given (AAMVA's three decimals), rounded to cents at the end. */
export const graduatedExact = (bands: MarginBand[], quantity: number): number => {
  let total = 0;
  for (const band of bands.slice().sort((a, b) => a.minQuantity - b.minQuantity)) {
    if (quantity < band.minQuantity) break;
    const top = band.maxQuantity == null ? quantity : Math.min(band.maxQuantity, quantity);
    total += (top - band.minQuantity + 1) * band.ratePerUnit;
  }
  return Math.round(total * 100) / 100;
};

export const money = (n: number | null | undefined, decimals = 2): string =>
  n == null
    ? '—'
    : `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
